import { z } from "zod/v3";

// ---------------------------------------------------------------------------
// Contact Information Classification
// ---------------------------------------------------------------------------

export const ContactInfoClassificationSchema = z.object({
  hasFullName: z.boolean().describe("Whether the resume contains the candidate's full name"),
  hasEmail: z.boolean().describe("Whether an email address is present"),
  hasPhoneNumber: z.boolean().describe("Whether a phone number is present"),
  hasLocation: z.boolean().describe("Whether a city/state/country-level location is present"),
  hasLinkedIn: z.boolean().describe("Whether a LinkedIn profile URL is present"),
  hasPortfolio: z.boolean().describe("Whether a portfolio, GitHub, or personal website is present"),
  hasFullStreetAddress: z
    .boolean()
    .describe("Whether a full street address is present (privacy concern)"),
});

// ---------------------------------------------------------------------------
// Summary / Objective Classification
// ---------------------------------------------------------------------------

export const SummaryClassificationSchema = z.object({
  present: z.boolean().describe("Whether a summary or objective section exists"),
  type: z
    .enum(["professional_summary", "objective_statement", "none"])
    .describe("Type of summary section"),
  length: z
    .enum(["too_short", "appropriate", "too_long"])
    .describe("Whether the summary length is appropriate (2-4 sentences = appropriate)"),
  hasQuantifiableAchievements: z
    .boolean()
    .describe("Whether the summary includes quantifiable achievements or metrics"),
  isBuzzwordHeavy: z
    .boolean()
    .describe("Whether the summary is mostly filler buzzwords without substance"),
  mentionsTargetRole: z
    .boolean()
    .describe("Whether the summary mentions a target role or career direction"),
});

// ---------------------------------------------------------------------------
// Experience Entry Classification
// ---------------------------------------------------------------------------

export const ExperienceEntryClassificationSchema = z.object({
  hasJobTitle: z.boolean().describe("Whether a job/position title is present"),
  hasCompanyName: z.boolean().describe("Whether the company/organization name is present"),
  hasDates: z.boolean().describe("Whether employment dates are present"),
  hasLocation: z.boolean().describe("Whether a work location is mentioned"),
  bulletStyle: z
    .enum(["achievement_focused", "duty_focused", "mixed", "paragraph_form"])
    .describe("Whether bullets focus on achievements, duties, a mix, or are paragraph-form"),
  actionVerbUsage: z
    .enum(["all", "most", "few", "none"])
    .describe("How many bullets start with strong action verbs"),
  hasQuantifiedResults: z
    .enum(["many", "some", "none"])
    .describe("How many bullets include quantifiable results or metrics"),
  bulletCount: z
    .enum(["too_few", "appropriate", "too_many"])
    .describe("Whether the number of bullets is appropriate (3-6 per role)"),
});

// ---------------------------------------------------------------------------
// Education Entry Classification
// ---------------------------------------------------------------------------

export const EducationEntryClassificationSchema = z.object({
  hasDegree: z.boolean().describe("Whether a degree name/type is present"),
  hasInstitution: z.boolean().describe("Whether the institution name is present"),
  hasGradDate: z.boolean().describe("Whether a graduation or attendance date is present"),
  hasGPA: z.boolean().describe("Whether a GPA is listed"),
  hasRelevantCoursework: z
    .boolean()
    .describe("Whether relevant coursework or academic projects are mentioned"),
});

// ---------------------------------------------------------------------------
// Skills Classification
// ---------------------------------------------------------------------------

export const SkillsClassificationSchema = z.object({
  present: z.boolean().describe("Whether a skills section exists"),
  organization: z
    .enum(["categorized", "flat_list", "inline_text", "none"])
    .describe("How skills are organized"),
  hasTechnicalSkills: z
    .boolean()
    .describe("Whether technical or domain-specific skills are present"),
  hasSoftSkillsOnly: z
    .boolean()
    .describe("Whether the section contains ONLY soft skills (no technical skills)"),
});

// ---------------------------------------------------------------------------
// Format / Structure Classification
// ---------------------------------------------------------------------------

export const FormatClassificationSchema = z.object({
  usesStandardHeadings: z
    .boolean()
    .describe("Whether standard ATS-recognizable section headings are used"),
  hasConsistentDateFormat: z.boolean().describe("Whether dates use a consistent format throughout"),
  hasConsistentFormatting: z
    .boolean()
    .describe("Whether formatting (fonts, spacing, alignment) is consistent"),
  lengthAssessment: z
    .enum(["too_short", "appropriate", "too_long"])
    .describe("Whether the resume length is appropriate for experience level"),
  hasTablesOrColumns: z
    .enum(["none", "simple", "complex"])
    .describe("Whether tables or multi-column layouts are used"),
  hasGraphicsOrImages: z
    .boolean()
    .describe("Whether graphics, images, or icons are detected (ATS unfriendly)"),
});

// ---------------------------------------------------------------------------
// Keywords Classification
// ---------------------------------------------------------------------------

export const MissingKeywordSchema = z.object({
  keyword: z.string().describe("Important keyword that is absent from the resume"),
  suggestedSection: z.string().describe("Which resume section this keyword should be added to"),
});

export const KeywordsClassificationSchema = z.object({
  detectedIndustry: z.string().describe("Best-guess industry inferred from the resume"),
  industryConfidence: z
    .enum(["high", "medium", "low"])
    .describe("Confidence in the detected industry"),
  foundKeywords: z
    .array(z.string().describe("A keyword or skill phrase found in the resume"))
    .describe("Keywords and skills detected in the resume text"),
  missingKeywords: z
    .array(MissingKeywordSchema)
    .describe("Important industry keywords absent from the resume"),
  keywordDistribution: z
    .enum(["well_distributed", "clustered", "sparse"])
    .describe("How keywords are distributed across sections"),
  hasKeywordStuffing: z
    .boolean()
    .describe("Whether keywords appear unnaturally repeated or stuffed"),
});

// ---------------------------------------------------------------------------
// Date Consistency Classification
// ---------------------------------------------------------------------------

export const DateConsistencyClassificationSchema = z.object({
  isConsistent: z.boolean().describe("Whether date formats are consistent across the resume"),
  hasGaps: z.boolean().describe("Whether there are unexplained gaps in employment timeline"),
  gapDetails: z
    .array(z.string().describe("Description of a timeline gap"))
    .optional()
    .describe("Details about detected gaps"),
});

// ---------------------------------------------------------------------------
// Overall Signals
// ---------------------------------------------------------------------------

const SectionDetectedEnum = z.enum([
  "contact",
  "summary",
  "experience",
  "education",
  "skills",
  "projects",
  "certifications",
  "awards",
  "volunteer",
  "languages",
  "publications",
  "references",
]);

export const OverallSignalsClassificationSchema = z.object({
  isATSParseable: z
    .boolean()
    .describe("Whether the resume structure is parseable by typical ATS systems"),
  hasSpellingOrGrammarIssues: z
    .boolean()
    .describe("Whether noticeable spelling or grammar issues are present"),
  toneIsProfessional: z.boolean().describe("Whether the tone is professional throughout"),
  sectionsDetected: z
    .array(SectionDetectedEnum)
    .describe("Which standard resume sections were detected"),
});

// ---------------------------------------------------------------------------
// Resume Snippets — verbatim extractions from the actual resume
// ---------------------------------------------------------------------------

export const ResumeSnippetsSchema = z.object({
  summaryText: z
    .string()
    .optional()
    .describe(
      "The FIRST 1-2 sentences of the summary/objective section, copied verbatim (max 200 chars). Empty if no summary exists.",
    ),
  weakestBullet: z
    .string()
    .optional()
    .describe(
      "The single weakest experience bullet point from the resume, copied verbatim. " +
        "Choose the most duty-focused, vague, or unquantified bullet.",
    ),
  strongestBullet: z
    .string()
    .optional()
    .describe(
      "The single strongest experience bullet point from the resume, copied verbatim. " +
        "Choose the most achievement-focused, quantified, and impactful bullet.",
    ),
  buzzwordExample: z
    .string()
    .optional()
    .describe(
      "A phrase or sentence from the resume that is heavy on buzzwords without substance. " +
        "Copied verbatim. Empty if no buzzwords detected.",
    ),
  skillsSnippet: z
    .string()
    .optional()
    .describe(
      "A short representative excerpt from the skills section showing how skills are listed, " +
        "copied verbatim (first 100 chars max). Empty if no skills section.",
    ),
});

// ---------------------------------------------------------------------------
// ATS Rewrite Schema — Pass 2 output for contextual improvement suggestions
// ---------------------------------------------------------------------------

export const ATSRewriteSchema = z.object({
  improvedSummary: z
    .string()
    .describe(
      "A rewritten version of the summary with quantifiable metrics added. " +
        "Keep the same role, domain, and companies — only add numbers and measurable outcomes. " +
        "Max 2 sentences / 200 chars. Empty string if no summary was provided.",
    ),
  improvedWeakBullet: z
    .string()
    .describe(
      "A rewritten version of the weakest bullet as an achievement-focused bullet. " +
        "Start with a strong action verb, keep the same topic/context, add a plausible metric. " +
        "Max 1 sentence / 150 chars. Empty string if no bullet was provided.",
    ),
  improvedSkillsSnippet: z
    .string()
    .describe(
      "A rewritten version of the skills snippet in categorized format " +
        '(e.g., "Frontend: React, TypeScript | Backend: Node.js, PostgreSQL"). ' +
        "Use ONLY skills from the original snippet. Max 150 chars. Empty string if no skills were provided.",
    ),
});

// ---------------------------------------------------------------------------
// Root Classification Schema
// ---------------------------------------------------------------------------

export const ATSClassificationSchema = z.object({
  contactInfo: ContactInfoClassificationSchema,
  summary: SummaryClassificationSchema,
  experiences: z.array(ExperienceEntryClassificationSchema),
  education: z.array(EducationEntryClassificationSchema),
  skills: SkillsClassificationSchema,
  format: FormatClassificationSchema,
  keywords: KeywordsClassificationSchema,
  dateConsistency: DateConsistencyClassificationSchema,
  overallSignals: OverallSignalsClassificationSchema,
  snippets: ResumeSnippetsSchema.optional(),
});

// ---------------------------------------------------------------------------
// Groq-compatible variant — all properties required, .nullable() instead of .optional()
// Groq rejects schemas where any property is missing from `required`.
// ---------------------------------------------------------------------------

export const GroqDateConsistencyClassificationSchema = z.object({
  isConsistent: z.boolean().describe("Whether date formats are consistent across the resume"),
  hasGaps: z.boolean().describe("Whether there are unexplained gaps in employment timeline"),
  gapDetails: z
    .array(z.string().describe("Description of a timeline gap"))
    .nullable()
    .describe("Details about detected gaps (null if none)"),
});

const GroqOverallSignalsClassificationSchema = z.object({
  isATSParseable: z
    .boolean()
    .describe("Whether the resume structure is parseable by typical ATS systems"),
  hasSpellingOrGrammarIssues: z
    .boolean()
    .describe("Whether noticeable spelling or grammar issues are present"),
  toneIsProfessional: z.boolean().describe("Whether the tone is professional throughout"),
  sectionsDetected: z.array(z.string()).describe("Which standard resume sections were detected"),
});

const GroqResumeSnippetsSchema = z.object({
  summaryText: z
    .string()
    .nullable()
    .describe(
      "The FIRST 1-2 sentences of the summary/objective section, copied verbatim (max 200 chars). Null if no summary exists.",
    ),
  weakestBullet: z
    .string()
    .nullable()
    .describe(
      "The single weakest experience bullet point from the resume, copied verbatim. " +
        "Choose the most duty-focused, vague, or unquantified bullet. Null if none.",
    ),
  strongestBullet: z
    .string()
    .nullable()
    .describe(
      "The single strongest experience bullet point from the resume, copied verbatim. " +
        "Choose the most achievement-focused, quantified, and impactful bullet. Null if none.",
    ),
  buzzwordExample: z
    .string()
    .nullable()
    .describe(
      "A phrase or sentence from the resume that is heavy on buzzwords without substance. " +
        "Copied verbatim. Null if no buzzwords detected.",
    ),
  skillsSnippet: z
    .string()
    .nullable()
    .describe(
      "A short representative excerpt from the skills section showing how skills are listed, " +
        "copied verbatim (first 100 chars max). Null if no skills section.",
    ),
});

export const GroqATSClassificationSchema = z.object({
  contactInfo: ContactInfoClassificationSchema,
  summary: SummaryClassificationSchema,
  experiences: z.array(ExperienceEntryClassificationSchema),
  education: z.array(EducationEntryClassificationSchema),
  skills: SkillsClassificationSchema,
  format: FormatClassificationSchema,
  keywords: KeywordsClassificationSchema,
  dateConsistency: GroqDateConsistencyClassificationSchema,
  overallSignals: GroqOverallSignalsClassificationSchema,
  snippets: GroqResumeSnippetsSchema,
});

export type ATSClassification = z.infer<typeof ATSClassificationSchema>;
export type ResumeSnippets = z.infer<typeof ResumeSnippetsSchema>;
export type ATSRewrite = z.infer<typeof ATSRewriteSchema>;
export type ContactInfoClassification = z.infer<typeof ContactInfoClassificationSchema>;
export type SummaryClassification = z.infer<typeof SummaryClassificationSchema>;
export type ExperienceEntryClassification = z.infer<typeof ExperienceEntryClassificationSchema>;
export type EducationEntryClassification = z.infer<typeof EducationEntryClassificationSchema>;
export type SkillsClassification = z.infer<typeof SkillsClassificationSchema>;
export type FormatClassification = z.infer<typeof FormatClassificationSchema>;
export type KeywordsClassification = z.infer<typeof KeywordsClassificationSchema>;
export type DateConsistencyClassification = z.infer<typeof DateConsistencyClassificationSchema>;
export type OverallSignalsClassification = z.infer<typeof OverallSignalsClassificationSchema>;
export type SectionDetected = z.infer<typeof SectionDetectedEnum>;
